import { ComponentProps } from 'react';
import { useField } from 'formik';
import { FieldPanelInput } from 'styles/v2/Styles.styled';
import SmartLabel from '../../labels/SmartLabel';
import SelectFormField from '../SelectFormField';
import VirtualizedMenuList from './VirtualizedMenuList ';
import type { Option } from 'types/types';
import { TooltipPosition } from 'components/controls/Tooltip';
import { FormatOptionLabelMeta, MultiValue, SingleValue } from 'react-select';
import { DropdownIndicator } from 'components/v2/epd-compiler-inputs/common-icons';
import { ReactComponent as CheckIconComponent } from 'images/v2/icons/svg/fill/svg/check.svg';

type MultiProps =
  | {
      isMulti: true;
      onChange?: (value: MultiValue<Option>) => void;
    }
  | {
      onChange?: (value: SingleValue<Option>) => void;
    };

type Props = {
  label?: string;
  labelStyle?: React.CSSProperties;
  tooltipPosition?: TooltipPosition;
  name: string;
  tooltip?: string;
  error?: string;
  required?: boolean;
  options: Option[] | undefined | null;
  disabled?: boolean;
  isMulti?: boolean;
  isVirtualized?: boolean;
  onChange?(value: any): void;
} & Omit<ComponentProps<typeof SelectFormField>, 'onChange'> &
  MultiProps;

const formatOptionLabel = (option: any, meta: FormatOptionLabelMeta<any>) => {
  const { context, selectValue } = meta;

  if (context === 'menu') {
    const isSelected = Array.isArray(selectValue) && selectValue.some((selected) => selected.value === option.value);

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <span>{option.label}</span>
        {isSelected && <CheckIconComponent />}
      </div>
    );
  }

  return option.label;
};

const SelectDataField: React.FC<Props> = ({
  label,
  labelStyle,
  name,
  tooltip,
  error,
  required,
  options,
  disabled,
  isMulti,
  tooltipPosition,
  isVirtualized = false,
  ...props
}) => {
  const [field] = useField(name);
  const hasValue = isMulti ? (field?.value?.length || 0) > 0 : !!field?.value;
  const showRequired = !!required && !disabled;

  return (
    <FieldPanelInput>
      {label && (
        <SmartLabel
          label={label}
          required={showRequired}
          labelStyle={labelStyle}
          tooltip={tooltip}
          tooltipPosition={tooltipPosition}
          hasValue={hasValue}
          error={error}
        />
      )}
      {isVirtualized ? (
        <SelectFormField
          name={name}
          options={options}
          isDisabled={disabled}
          isMulti={isMulti}
          {...props}
          components={{ ...props.components, MenuList: VirtualizedMenuList, DropdownIndicator }}
          formatOptionLabel={formatOptionLabel}
        />
      ) : (
        <SelectFormField
          name={name}
          options={options}
          isDisabled={disabled}
          isMulti={isMulti}
          components={{ DropdownIndicator }}
          formatOptionLabel={formatOptionLabel}
          {...props}
        />
      )}
    </FieldPanelInput>
  );
};

export default SelectDataField;
